import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { PanelService } from '@connect-services/client/services/panel.service';

export enum ModeUiSource {
    MAP = 'map',
    TABLE = 'table',
}

@Injectable({
    providedIn: 'root',
})
export class EditModeLayerPanelService {
    private lastPanelState: { enabled: boolean; projectId: string | null } = {
        enabled: false,
        projectId: null,
    };
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly panelService = inject(PanelService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    public async handleEditModePanelChange(active: boolean): Promise<void> {
        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        if (this.contentLayoutService.isDataTableView()) {
            await this.ensureLayerPanelClosed(projectId);

            return;
        }

        await this.handleMapSourcePanelChange(active, projectId);
    }

    private closeLayerPanelForEditMode(projectId: string): void {
        const { layersUrlPrefix } = this.getUrls(projectId);

        if (!this.router.url.startsWith(layersUrlPrefix)) {
            return;
        }

        this.panelService.closePanel();
    }

    private async ensureLayerPanelClosed(projectId: string): Promise<void> {
        const isLayerPanelOpen = this.panelService.currentPanel() === ActionsEnum.LAYER;
        if (!isLayerPanelOpen) {
            return;
        }

        await this.closeLayerPanelForEditMode(projectId);
    }

    private getUrls(projectId: string): { layersUrlPrefix: string; projectRootUrl: string } {
        const projectRootUrl = `/projects/${projectId}`;

        return {
            layersUrlPrefix: `${projectRootUrl}/layers`,
            projectRootUrl,
        };
    }

    private async handleMapSourcePanelChange(active: boolean, projectId: string): Promise<void> {
        if (this.shouldSkip(active, projectId)) {
            return;
        }

        if (active) {
            await this.openLayerPanelForEditMode(projectId);
            this.lastPanelState = { enabled: true, projectId };

            return;
        }

        await this.closeLayerPanelForEditMode(projectId);
        this.lastPanelState = { enabled: false, projectId };
    }

    private isExactlyOnRoot(url: string, root: string): boolean {
        return url === root || url === `${root}/`;
    }

    private async openLayerPanelForEditMode(projectId: string): Promise<void> {
        const { layersUrlPrefix } = this.getUrls(projectId);

        const url = this.router.url;
        const isExactlyOnLayersRoot = this.isExactlyOnRoot(url, layersUrlPrefix);

        if (this.panelService.currentPanel() !== ActionsEnum.LAYER) {
            this.panelService.setPanel(null);
        }

        this.panelService.setPanel(ActionsEnum.LAYER);

        if (isExactlyOnLayersRoot) {
            return;
        }

        await this.safeNavigate(['projects', projectId, 'layers']);
    }

    private async safeNavigate(commands: string[]): Promise<void> {
        try {
            await this.router.navigate(commands);
        } catch {
            /* empty */
        }
    }

    private shouldSkip(active: boolean, projectId: string): boolean {
        if (this.lastPanelState.projectId !== projectId) {
            return false;
        }

        const { projectRootUrl, layersUrlPrefix } = this.getUrls(projectId);
        const url = this.router.url;
        const isExactlyOnLayersRoot = this.isExactlyOnRoot(url, layersUrlPrefix);

        if (active) {
            return (
                this.lastPanelState.enabled &&
                this.panelService.currentPanel() === ActionsEnum.LAYER &&
                isExactlyOnLayersRoot
            );
        }

        return (
            !this.lastPanelState.enabled &&
            this.panelService.currentPanel() !== ActionsEnum.LAYER &&
            this.isExactlyOnRoot(url, projectRootUrl)
        );
    }
}
